/**
 * coreServicesClient.js - Cliente de Integración WE360 Core Services
 *
 * Especificación Técnica: Integración web <-> core - we360 - sync - users (v1.0.0)
 * Endpoints Oficiales:
 *  - GET  /api/user/daily-dashboard
 *  - GET  /api/user/daily-recommendations
 *  - POST /api/user/sync-web-user
 *
 * Principios Clínicos de Interoperabilidad (Protocolo Dr. Miguel Álvarez):
 *  1. Reconciliación Aritmética de Atwater (100% Exacta):
 *     Total Kcal = (4 * Carbs) + (4 * Proteína) + (9 * Grasa) + (7 * Alcohol)
 *  2. Regla Cero Calorías Netas ("Sin comerse el ejercicio"):
 *     Presupuesto Restante = Meta Calórica - Comida Consumida (el ejercicio es bonus metabólico)
 *  3. Hidratación Dinámica (Protocolo SMA-303):
 *     Meta Hídrica = Base (ml) + (+500 ml por cada 500 kcal de ejercicio activo)
 */

export const DEFAULT_CORE_API_URL = "https://we360-dev-ep.saluddigital360.com/api";
export const DEFAULT_CORE_API_KEY = "Ho6cxFb2pbTPol4yKIRh6tcrQeaBrFNE";
export const DEFAULT_DEV_BOT_PHONE = "+1 (555) 178-1374";
export const DEFAULT_STAGING_BOT_PHONE = "+1 (877) 874-2737";
export const DEFAULT_CORE_BOT_PHONE = DEFAULT_DEV_BOT_PHONE;
export const WE360_WHATSAPP_BOT_NUMBER = DEFAULT_DEV_BOT_PHONE;

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL =
  RAW_BACKEND_URL === "http://localhost:8001" && typeof window !== "undefined" && window.location?.hostname !== "localhost"
    ? ""
    : RAW_BACKEND_URL;

/**
 * Normaliza la URL de Core Services:
 * Si se ingresa el dominio base de Core Services (we360-*-ep.saluddigital360.com)
 * sin el prefijo /api, lo añade automáticamente para evitar errores 404.
 */
export function normalizeCoreApiUrl(url) {
  if (!url || typeof url !== "string") return "";
  const clean = url.trim().replace(/\/+$/, "");
  if (/we360-(dev|stg|prod)-ep\.saluddigital360.com$/i.test(clean)) {
    return `${clean}/api`;
  }
  return clean;
}

/**
 * Construye la URL del endpoint a través del proxy server-to-server del backend FastAPI.
 * Esto elimina restricciones de CORS del navegador comunicándose con el backend
 * (same-origin en Firebase Hosting o localhost:8001 en dev), el cual realiza la consulta
 * server-to-server directa hacia Core Services / Leandro.
 *
 * @param {string} endpointPath - Subruta relativa (ej: "user/daily-dashboard")
 * @returns {string} URL formateada para el proxy
 */
export function getCoreProxyUrl(endpointPath = "") {
  const cleanPath = String(endpointPath).replace(/^\/+/, "");
  const base = BACKEND_URL ? BACKEND_URL.replace(/\/+$/, "") : "";
  return `${base}/api/core-services/proxy/${cleanPath}`;
}

export const CORE_API_URL = process.env.REACT_APP_CORE_API_URL || DEFAULT_CORE_API_URL;
export const CORE_API_KEY =
  process.env.REACT_APP_CORE_API_KEY && process.env.REACT_APP_CORE_API_KEY !== "02e9cfbbd67ba5bb39cefd96f432ea7064d7df64336c53e0"
    ? process.env.REACT_APP_CORE_API_KEY
    : DEFAULT_CORE_API_KEY;

export const CORE_CONFIG_STORAGE_KEY = "WE360_CORE_CONFIG_OVERRIDE";
export const CORE_FIRESTORE_CACHE_KEY = "WE360_CORE_CONFIG_FIRESTORE_CACHE";

let memoryFirestoreConfig = null;

/**
 * Entornos preconfigurados para Core Services
 */
export const CORE_ENVIRONMENTS = {
  dev: {
    id: "dev",
    name: "Desarrollo (DEV)",
    badge: "DEV",
    badgeColor: "emerald",
    url: "https://we360-dev-ep.saluddigital360.com/api",
    apiKey: "Ho6cxFb2pbTPol4yKIRh6tcrQeaBrFNE",
    botPhone: "+1 (555) 178-1374",
    description: "Servidor de desarrollo activo de Leandro Mayor"
  },
  staging: {
    id: "staging",
    name: "Staging (Homologación)",
    badge: "STG",
    badgeColor: "amber",
    url: "https://we360-stg-ep.saluddigital360.com/api",
    apiKey: "Ho6cxFb2pbTPol4yKIRh6tcrQeaBrFNE",
    botPhone: "+1 (877) 874-2737",
    description: "Ambiente de pre-producción y validación médica oficial"
  },
  prod: {
    id: "prod",
    name: "Producción Oficial",
    badge: "PROD",
    badgeColor: "blue",
    url: "",
    apiKey: "",
    botPhone: "",
    description: "Servidor productivo oficial (Por configurar)"
  },
  custom: {
    id: "custom",
    name: "Personalizado",
    badge: "CUSTOM",
    badgeColor: "purple",
    url: "",
    apiKey: "",
    botPhone: "",
    description: "Configuración manual de endpoint y credenciales"
  }
};

/**
 * Actualiza la caché en memoria y localStorage de Firestore y emite un evento global
 */
export function updateCachedFirestoreConfig(config) {
  memoryFirestoreConfig = config || null;
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      if (config) {
        window.localStorage.setItem(CORE_FIRESTORE_CACHE_KEY, JSON.stringify(config));
      } else {
        window.localStorage.removeItem(CORE_FIRESTORE_CACHE_KEY);
      }
    } catch (e) {}
  }
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent("we360_core_config_changed", { detail: getCoreServicesConfig() }));
  }
}

/**
 * Obtiene la configuración activa de Core Services resolviendo en orden de prioridad:
 * 1. Override local en localStorage (para pruebas aisladas de desarrollo)
 * 2. Configuración global almacenada en Firestore (para todo el ambiente)
 * 3. Variables de entorno inyectadas al compilar (.env)
 * 4. Fallbacks seguros por defecto
 */
export function getCoreServicesConfig() {
  const isStagingHost = typeof window !== "undefined" && window.location?.hostname && (
    window.location.hostname.includes("stg") ||
    window.location.hostname.includes("staging") ||
    window.location.hostname.includes("we360-stg")
  );
  const isProdHost = typeof window !== "undefined" && window.location?.hostname && (
    window.location.hostname === "we360.ai" ||
    window.location.hostname === "app.we360.ai"
  );

  const envName = (process.env.REACT_APP_ENV === "staging" || isStagingHost)
    ? "staging"
    : (process.env.REACT_APP_ENV === "production" || isProdHost)
    ? "prod"
    : "dev";

  const envPreset = CORE_ENVIRONMENTS[envName] || CORE_ENVIRONMENTS.dev;
  const sanitizeKey = (key, fallback = envPreset.apiKey || DEFAULT_CORE_API_KEY) => {
    if (!key) return fallback;
    if (key === "02e9cfbbd67ba5bb39cefd96f432ea7064d7df64336c53e0") {
      return fallback || DEFAULT_CORE_API_KEY;
    }
    return key;
  };

  const defaultBotPhone = envName === "staging" ? DEFAULT_STAGING_BOT_PHONE : DEFAULT_DEV_BOT_PHONE;

  const baseDefaults = {
    environment: envName,
    apiUrl: process.env.REACT_APP_CORE_API_URL || envPreset.url || DEFAULT_CORE_API_URL,
    apiKey: sanitizeKey(process.env.REACT_APP_CORE_API_KEY, envPreset.apiKey || DEFAULT_CORE_API_KEY),
    botPhone: process.env.REACT_APP_WHATSAPP_BOT_NUMBER || envPreset.botPhone || defaultBotPhone,
    forceFallback: false,
    hasOverride: false,
    isLocalOverride: false,
    isGlobalFirestore: false,
    updatedAt: null,
    updatedBy: null
  };

  if (typeof window === "undefined" || !window.localStorage) {
    return baseDefaults;
  }

  // 1. Override local en localStorage
  try {
    const rawLocal = window.localStorage.getItem(CORE_CONFIG_STORAGE_KEY);
    if (rawLocal) {
      const parsed = JSON.parse(rawLocal);
      if (parsed && typeof parsed === "object") {
        return {
          environment: parsed.environment || "custom",
          apiUrl: parsed.apiUrl !== undefined ? parsed.apiUrl : baseDefaults.apiUrl,
          apiKey: parsed.apiKey !== undefined ? sanitizeKey(parsed.apiKey, "") : baseDefaults.apiKey,
          botPhone: parsed.botPhone !== undefined ? parsed.botPhone : baseDefaults.botPhone,
          forceFallback: Boolean(parsed.forceFallback),
          hasOverride: true,
          isLocalOverride: true,
          isGlobalFirestore: false,
          updatedAt: parsed.updatedAt || null,
          updatedBy: "Local Override"
        };
      }
    }
  } catch (e) {
    console.warn("[coreServicesClient] Error al leer configuración local:", e.message);
  }

  // 2. Configuración global desde Firestore (en memoria o caché local persistida)
  let fsConfig = memoryFirestoreConfig;
  if (!fsConfig) {
    try {
      const rawFs = window.localStorage.getItem(CORE_FIRESTORE_CACHE_KEY);
      if (rawFs) {
        fsConfig = JSON.parse(rawFs);
        memoryFirestoreConfig = fsConfig;
      }
    } catch (e) {}
  }

  if (fsConfig && typeof fsConfig === "object") {
    return {
      environment: fsConfig.environment || baseDefaults.environment,
      apiUrl: fsConfig.apiUrl !== undefined ? fsConfig.apiUrl : baseDefaults.apiUrl,
      apiKey: fsConfig.apiKey !== undefined ? sanitizeKey(fsConfig.apiKey, "") : baseDefaults.apiKey,
      botPhone: fsConfig.botPhone !== undefined ? fsConfig.botPhone : baseDefaults.botPhone,
      forceFallback: Boolean(fsConfig.forceFallback),
      hasOverride: true,
      isLocalOverride: false,
      isGlobalFirestore: true,
      updatedAt: fsConfig.updatedAt || null,
      updatedBy: fsConfig.updatedBy || null
    };
  }

  return baseDefaults;
}

/**
 * Consulta la configuración global almacenada en Firestore (config/coreServices)
 */
export async function fetchCoreServicesConfigFromFirestore() {
  try {
    const { db } = await import("@/firebase");
    const { doc, getDoc } = await import("firebase/firestore");
    const snap = await getDoc(doc(db, "config", "coreServices"));
    if (snap.exists()) {
      const data = snap.data();
      updateCachedFirestoreConfig(data);
      return data;
    }
    return null;
  } catch (err) {
    console.warn("[coreServicesClient] Error al consultar Firestore:", err.message);
    return null;
  }
}

/**
 * Guarda la configuración global en Firestore (config/coreServices) con trazabilidad de admin
 * Aplica en tiempo real a todos los usuarios del ambiente activo.
 */
export async function saveCoreServicesConfigToFirestore(newConfig = {}, userEmail = null) {
  const current = getCoreServicesConfig();
  const toSave = {
    environment: newConfig.environment || current.environment || "dev",
    apiUrl: newConfig.apiUrl !== undefined ? String(newConfig.apiUrl).trim() : current.apiUrl,
    apiKey: newConfig.apiKey !== undefined ? String(newConfig.apiKey).trim() : current.apiKey,
    botPhone: newConfig.botPhone !== undefined ? String(newConfig.botPhone).trim() : current.botPhone,
    forceFallback: newConfig.forceFallback !== undefined ? Boolean(newConfig.forceFallback) : current.forceFallback,
    updatedAt: new Date().toISOString(),
    updatedBy: userEmail || "itadmin@saluddigital360.com"
  };

  const { db } = await import("@/firebase");
  const { doc, setDoc } = await import("firebase/firestore");
  await setDoc(doc(db, "config", "coreServices"), toSave, { merge: true });

  // Limpiar cualquier override local para asegurar precedencia global
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem(CORE_CONFIG_STORAGE_KEY);
  }

  updateCachedFirestoreConfig(toSave);
  return getCoreServicesConfig();
}

/**
 * Restablece la configuración global en Firestore eliminando el documento y restaurando defaults del ambiente
 */
export async function resetCoreServicesConfigInFirestore() {
  try {
    const { db } = await import("@/firebase");
    const { doc, deleteDoc } = await import("firebase/firestore");
    await deleteDoc(doc(db, "config", "coreServices"));
  } catch (err) {
    console.warn("[coreServicesClient] Error al eliminar documento en Firestore:", err.message);
  }

  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem(CORE_CONFIG_STORAGE_KEY);
  }

  updateCachedFirestoreConfig(null);
  return getCoreServicesConfig();
}

/**
 * Suscripción en tiempo real a cambios de configuración en Firestore
 */
export function subscribeToCoreServicesConfig(callback) {
  if (typeof window === "undefined") return () => {};

  let unsubscribeSnapshot = null;
  let isSubscribed = false;

  const startListener = async () => {
    if (isSubscribed) return;
    try {
      const { db } = await import("@/firebase");
      const { doc, onSnapshot } = await import("firebase/firestore");

      unsubscribeSnapshot = onSnapshot(
        doc(db, "config", "coreServices"),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            updateCachedFirestoreConfig(data);
            if (typeof callback === "function") callback(getCoreServicesConfig());
          } else {
            updateCachedFirestoreConfig(null);
            if (typeof callback === "function") callback(getCoreServicesConfig());
          }
        },
        (error) => {
          console.debug("[coreServicesClient] Snapshot subscription notice:", error.message);
        }
      );
      isSubscribed = true;
    } catch (err) {
      console.debug("[coreServicesClient] Error al iniciar listener de Firestore:", err.message);
    }
  };

  let unsubscribeAuth = () => {};
  import("@/firebase").then(({ auth }) => {
    const { onAuthStateChanged } = require("firebase/auth");
    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        startListener();
      } else {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
          isSubscribed = false;
        }
      }
    });
  }).catch(() => {});

  return () => {
    if (typeof unsubscribeAuth === "function") unsubscribeAuth();
    if (unsubscribeSnapshot) {
      unsubscribeSnapshot();
      unsubscribeSnapshot = null;
      isSubscribed = false;
    }
  };
}

/**
 * Persiste una configuración personalizada en localStorage y notifica cambios a la UI
 */
export function setCoreServicesConfig(newConfig = {}) {
  if (typeof window === "undefined" || !window.localStorage) {
    return getCoreServicesConfig();
  }

  const current = getCoreServicesConfig();
  const toSave = {
    environment: newConfig.environment || current.environment || "custom",
    apiUrl: newConfig.apiUrl !== undefined ? String(newConfig.apiUrl).trim() : current.apiUrl,
    apiKey: newConfig.apiKey !== undefined ? String(newConfig.apiKey).trim() : current.apiKey,
    botPhone: newConfig.botPhone !== undefined ? String(newConfig.botPhone).trim() : current.botPhone,
    forceFallback: newConfig.forceFallback !== undefined ? Boolean(newConfig.forceFallback) : current.forceFallback,
    updatedAt: new Date().toISOString()
  };

  try {
    window.localStorage.setItem(CORE_CONFIG_STORAGE_KEY, JSON.stringify(toSave));
    if (typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent("we360_core_config_changed", { detail: toSave }));
    }
  } catch (e) {
    console.error("[coreServicesClient] No se pudo guardar la configuración en localStorage:", e);
  }

  return getCoreServicesConfig();
}

/**
 * Restablece la configuración eliminando cualquier override local
 */
export function resetCoreServicesConfig() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(CORE_CONFIG_STORAGE_KEY);
      if (typeof window.dispatchEvent === "function") {
        window.dispatchEvent(new CustomEvent("we360_core_config_changed", { detail: null }));
      }
    } catch (e) {
      console.error("[coreServicesClient] Error al limpiar localStorage:", e);
    }
  }
  return getCoreServicesConfig();
}

/**
 * Helpers para acceder a los valores activos en tiempo de ejecución
 */
export function getActiveCoreApiUrl() {
  return getCoreServicesConfig().apiUrl;
}

export function getActiveCoreApiKey() {
  return getCoreServicesConfig().apiKey;
}

export function getActiveWhatsAppBotNumber() {
  return getCoreServicesConfig().botPhone;
}

/**
 * Realiza un ping de diagnóstico clínico al endpoint daily-dashboard
 */
export async function pingCoreServices(overrideConfig = null, testPhone = "584124281735") {
  const config = overrideConfig ? { ...getCoreServicesConfig(), ...overrideConfig } : getCoreServicesConfig();
  const startTime = Date.now();
  const cleanPhone = String(testPhone || config.botPhone || "584124281735").replace(/\D/g, "");

  if (config.forceFallback) {
    return {
      ok: true,
      status: 200,
      statusText: "OK (Modo Offline / Fallback)",
      latencyMs: 1,
      timestamp: new Date().toISOString(),
      url: `${config.apiUrl}/user/daily-dashboard (Offline)`,
      isOfflineMode: true,
      isAtwaterValid: true,
      data: DEMO_DAILY_DASHBOARD
    };
  }

  const rawApiUrl = config.apiUrl || CORE_API_URL;
  const normalizedBase = normalizeCoreApiUrl(rawApiUrl);
  const targetUrl = `${normalizedBase}/user/daily-dashboard?phone=${encodeURIComponent(cleanPhone)}`;
  const proxyUrl = getCoreProxyUrl(`user/daily-dashboard?phone=${encodeURIComponent(cleanPhone)}`);

  try {
    const res = await fetch(proxyUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-core-target-url": normalizedBase,
        "x-core-api-key": config.apiKey,
        "x-api-key": config.apiKey
      }
    });

    const latencyMs = Date.now() - startTime;
    let data = null;
    try {
      data = await res.json();
    } catch (parseErr) {
      // no es JSON
    }

    const isAtwaterValid = Boolean(
      res.ok &&
      data &&
      data.status === "Success" &&
      data.nutrition &&
      typeof data.nutrition.targetCalories === "number"
    );

    return {
      ok: res.ok,
      status: res.status,
      statusText: res.statusText || (res.ok ? "OK" : "Error"),
      latencyMs,
      timestamp: new Date().toISOString(),
      url: targetUrl,
      proxyUrl,
      isOfflineMode: false,
      isAtwaterValid,
      data,
      error: res.ok ? null : (data?.detail || data?.message || `HTTP ${res.status}: ${res.statusText || "Error del servidor"}`)
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      statusText: "Error de Conexión",
      latencyMs: Date.now() - startTime,
      timestamp: new Date().toISOString(),
      url: targetUrl,
      proxyUrl,
      isOfflineMode: false,
      isAtwaterValid: false,
      data: null,
      error: error.message || "Fallo de red o CORS"
    };
  }
}

/**
 * Datos Demo oficiales de la especificación técnica y sample real de Leandro (2026-09-30)
 */
export const DEMO_DAILY_DASHBOARD = {
  status: "Success",
  phone: "584124281735",
  date: "2026-09-30",
  timezone: "America/Caracas",
  user: {
    name: "Leandro Mayor",
    firstName: "Leandro",
    businessModel: "B2C",
    currentPhase: 5,
    tags: []
  },
  clinicalTrackingParameters: {
    bloodPressure: {
      name: "Nivel de Presión Arterial",
      systolic: null,
      diastolic: null,
      unit: "mmHg",
      status: "RISK",
      color: "yellow",
      colorHex: "#eab308",
      label: "Condición Hipertensiva Diagnosticada (Sin lectura hoy)"
    },
    bodyComposition: {
      name: "Composición Corporal y Peso",
      weightKg: 93.65,
      heightCm: 178,
      bmi: 29.6,
      waistCm: null,
      whtr: null,
      classification: "OVERWEIGHT",
      status: "RISK",
      color: "yellow",
      colorHex: "#eab308",
      label: "Sobrepeso (IMC 25 - 29.9)"
    },
    glycemicControl: {
      name: "Control Glucémico y Azúcar",
      glucoseMgDl: null,
      hba1c: null,
      unit: "mg/dL",
      status: "OPTIMAL",
      color: "green",
      colorHex: "#22c55e",
      label: "Control Glucémico Óptimo (70 - 99 mg/dL)"
    },
    emotionalStress: {
      name: "Estrés Emocional y SNA (HRV)",
      hrvMs: null,
      basalHrv: 45,
      stressLevel: "BAJO",
      status: "OPTIMAL",
      color: "green",
      colorHex: "#22c55e",
      label: "Tono Parasimpático Favorable / Recuperación Óptima"
    },
    physicalInactivity: {
      name: "Inactividad Física y Movimiento",
      steps: 5500,
      unit: "pasos",
      status: "RISK",
      color: "yellow",
      colorHex: "#eab308",
      label: "Actividad Moderada / Sedentarismo Leve (3,000 - 6,999 pasos)"
    },
    lipidProfile: {
      name: "Perfil Lipídico (c-LDL)",
      ldlMgDl: null,
      unit: "mg/dL",
      status: "OPTIMAL",
      color: "green",
      colorHex: "#22c55e",
      label: "Perfil Lipídico Controlado (c-LDL < 100 mg/dL)"
    }
  },
  nutrition: {
    targetCalories: 2134,
    consumedCalories: 875.5,
    remainingCalories: 1258.5,
    macros: {
      carbs: {
        targetGrams: 267,
        consumedGrams: 113.5
      },
      protein: {
        targetGrams: 107,
        consumedGrams: 52.5
      },
      fat: {
        targetGrams: 71,
        consumedGrams: 23.5
      }
    },
    meals: [
      {
        name: "Cereal Fitness con leche",
        calories: 387.5,
        category: "Desayuno",
        carbs_g: 57.5,
        protein_g: 13.5,
        fat_g: 11.5,
        alcohol_g: 0,
        caffeine_shots: 0,
        portion_factor: 1,
        portion_reason: "Porción individual completa (100%)",
        loggedAt: "2026-09-30T16:23:36.131Z"
      },
      {
        name: "Arroz blanco con pollo guisado",
        calories: 488,
        category: "Almuerzo",
        carbs_g: 56,
        protein_g: 39,
        fat_g: 12,
        alcohol_g: 0,
        caffeine_shots: 0,
        portion_factor: 1,
        portion_reason: "Porción individual completa (100%)",
        loggedAt: "2026-09-30T19:12:12.360Z"
      }
    ]
  },
  hydration: {
    targetMl: 3310,
    consumedMl: 2000,
    remainingMl: 1310
  },
  activity: {
    steps: 5500,
    activeCaloriesBurned: 550.1,
    bonusMetabolico: 550.1,
    exercises: [
      {
        activityName: "CrossFit (2 de 3 rondas)",
        steps: 0,
        durationMin: 30,
        caloriesBurned: 392,
        loggedAt: "2026-09-30T11:04:46.792Z"
      },
      {
        activity: "Actividad Física",
        steps: 5500,
        loadWeightKg: 0,
        durationMin: 55,
        caloriesBurned: 158.1,
        loggedAt: "2026-09-30T11:16:19.472Z"
      }
    ]
  }
};

/**
 * Datos Demo de Recomendaciones Clínicas según contrato del PDF (pág 5, 7-8)
 */
export const DEMO_DAILY_RECOMMENDATIONS = {
  status: "Success",
  phone: "584124281735",
  activeNudge: {
    title: "EMPUJÓN CLÍNICO ACTIVO: ESTIRAMIENTOS SUAVES 5 MIN",
    description: "5 a 10 minutos de movilidad articular y estiramientos suaves para liberar tensión neuromuscular y acelerar la recuperación post-entrenamiento.",
    timeFormatted: "11:45 AM",
    isActive: true
  },
  recommendations: [
    {
      agent: "Nutrición IA",
      badgeColor: "emerald",
      message: "Excelente aporte de proteínas de alta biodisponibilidad (87g). Tu síntesis muscular y saciedad están protegidas con 1332 kcal consumidas.",
      timeFormatted: "11:59 PM"
    },
    {
      agent: "Actividad Física IA",
      badgeColor: "blue",
      message: "Completaste WOD Especial CrossFit con 197.4 kcal de gasto activo. Receptores GLUT4 estimulados sin sobrecargar tu sistema.",
      timeFormatted: "09:30 PM"
    },
    {
      agent: "Manejo de Estrés",
      badgeColor: "purple",
      message: "Tu tono parasimpático y variabilidad cardíaca muestran una respuesta favorable tras la pausa activa de respiración consciente.",
      timeFormatted: "05:15 PM"
    }
  ]
};

/**
 * Determina si un mensaje de recomendación es un placeholder/mock estático
 * generado por defecto por los entornos de prueba, fallbacks de servidor o datos demo.
 */
export function isCannedRecommendationMessage(msg) {
  if (!msg || typeof msg !== "string") return false;
  const lower = msg.toLowerCase().trim();
  return (
    lower.includes("iniciando la jornada: planifica un desayuno con suficiente proteína") ||
    lower.includes("tu sesión activa de hoy ayudará a estimular los receptores glut4") ||
    lower.includes("tu tono parasimpático y variabilidad cardíaca muestran una respuesta favorable") ||
    lower.includes("excelente aporte de proteínas de alta biodisponibilidad (87g)") ||
    lower.includes("completaste wod especial crossfit") ||
    lower.includes("paseo postprandial: 10 a 15 minutos de caminata")
  );
}

/**
 * Determina si un activeNudge es un placeholder estático de prueba.
 */
export function isCannedActiveNudge(nudge) {
  if (!nudge) return false;
  const title = (nudge.title || "").toLowerCase();
  const desc = (nudge.description || "").toLowerCase();
  return (
    title.includes("estiramientos suaves 5 min") ||
    desc.includes("movilidad articular y estiramientos suaves")
  );
}

/**
 * Reconciliación Aritmética de Atwater (100% Exacta):
 * Total Kcal = (4 * Carbohidratos) + (4 * Proteínas) + (9 * Grasas) + (7 * Alcohol)
 */
export function calculateAtwaterCalories(carbsG = 0, proteinG = 0, fatG = 0, alcoholG = 0) {
  const c = Math.max(0, parseFloat(carbsG) || 0);
  const p = Math.max(0, parseFloat(proteinG) || 0);
  const f = Math.max(0, parseFloat(fatG) || 0);
  const a = Math.max(0, parseFloat(alcoholG) || 0);
  return Math.round(4 * c + 4 * p + 9 * f + 7 * a);
}

/**
 * Regla de Cero Calorías Netas:
 * Presupuesto Restante = Meta Calórica - Comida Consumida
 * El ejercicio NUNCA se suma de vuelta para comer más.
 */
export function calculateRemainingCalories(targetCalories = 0, consumedCalories = 0) {
  const target = Math.max(0, parseFloat(targetCalories) || 0);
  const consumed = Math.max(0, parseFloat(consumedCalories) || 0);
  return Math.max(0, parseFloat((target - consumed).toFixed(1)));
}

/**
 * Hidratación Dinámica (Protocolo SMA-303):
 * La meta de agua base del usuario se escala dinámicamente sumando +500 ml
 * por cada 500 kcal de ejercicio activo registradas en la jornada.
 *
 * @param {number} baseTargetMl - Meta base de agua en ml (ej. 2810 o 2500)
 * @param {number} activeCaloriesBurned - Calorías activas quemadas en el día
 * @returns {number} Meta de hidratación ajustada en ml
 */
export function calculateSma303HydrationTarget(baseTargetMl = 2500, activeCaloriesBurned = 0) {
  const base = Math.max(0, parseFloat(baseTargetMl) || 0);
  const burned = Math.max(0, parseFloat(activeCaloriesBurned) || 0);
  const bonusMl = Math.floor(burned / 500) * 500;
  return base + bonusMl;
}

/**
 * Genera el enlace de chat oficial para WhatsApp
 */
export function getWhatsAppChatUrl(phone, message = "Hola") {
  const targetPhone = phone || getActiveWhatsAppBotNumber() || WE360_WHATSAPP_BOT_NUMBER;
  const cleanPhone = String(targetPhone).replace(/\D/g, "");
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
}

/**
 * GET /api/user/daily-dashboard
 *
 * @param {string} phone - Número de teléfono internacional (ej: "584124281735")
 * @param {string} email - Correo del usuario (alternativo)
 * @param {string} date - Fecha ISO en formato YYYY-MM-DD
 * @param {object} options - Opciones adicionales ({ isDemo: boolean, fetchLive: boolean, forceFallback: boolean })
 * @returns {Promise<object>} Balance metabólico consolidado del día
 */
export async function getDailyDashboard(phone, email, date, options = {}) {
  const config = getCoreServicesConfig();
  const { isDemo = false, fetchLive = false, forceFallback = false } = options;

  if (forceFallback || config.forceFallback) {
    return DEMO_DAILY_DASHBOARD;
  }

  // Si estamos en demo y no se solicitó consulta en vivo, o no hay identificador
  if ((isDemo && !fetchLive) || (!phone && !email && !fetchLive)) {
    return isDemo ? DEMO_DAILY_DASHBOARD : null;
  }

  const isDemoUser = isDemo || phone === "584124281735";
  const targetPhone = phone || (isDemo ? "584124281735" : "");
  const targetEmail = email || "";

  if (!targetPhone && !targetEmail) {
    return isDemoUser ? DEMO_DAILY_DASHBOARD : null;
  }

  const query = new URLSearchParams();
  if (targetPhone) query.append("phone", targetPhone);
  if (targetEmail) query.append("email", targetEmail);
  if (date) query.append("date", date);

  const activeUrl = normalizeCoreApiUrl(config.apiUrl || CORE_API_URL);
  const activeKey = config.apiKey || CORE_API_KEY;
  const proxyUrl = getCoreProxyUrl(`user/daily-dashboard?${query.toString()}`);

  try {
    const res = await fetch(proxyUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-core-target-url": activeUrl,
        "x-core-api-key": activeKey,
        "x-api-key": activeKey
      }
    });

    if (!res.ok) {
      console.warn(`[coreServicesClient] Error ${res.status} al consultar daily-dashboard vía proxy`);
      return isDemoUser ? DEMO_DAILY_DASHBOARD : null;
    }

    const data = await res.json();
    return data;
  } catch (error) {
    console.warn("[coreServicesClient] Error de red en daily-dashboard vía proxy:", error.message);
    return isDemoUser ? DEMO_DAILY_DASHBOARD : null;
  }
}

/**
 * GET /api/user/daily-recommendations
 *
 * @param {string} phone - Número de teléfono internacional (ej: "584124281735")
 * @param {string} email - Correo del usuario (alternativo)
 * @param {number} limit - Límite de tarjetas de recomendación (def: 5)
 * @param {object} options - Opciones ({ isDemo: boolean, fetchLive: boolean, forceFallback: boolean, date?: string })
 * @returns {Promise<object>} activeNudge y recomendaciones de los 3 pilares
 */
export async function getDailyRecommendations(phone, email, limit = 5, options = {}) {
  const config = getCoreServicesConfig();
  const { isDemo = false, fetchLive = false, forceFallback = false } = options;

  if (forceFallback || config.forceFallback) {
    return DEMO_DAILY_RECOMMENDATIONS;
  }

  if ((isDemo && !fetchLive) || (!phone && !email && !fetchLive)) {
    return isDemo ? DEMO_DAILY_RECOMMENDATIONS : null;
  }

  const isDemoUser = isDemo || phone === "584124281735";
  const targetPhone = phone || (isDemo ? "584124281735" : "");
  const targetEmail = email || "";

  if (!targetPhone && !targetEmail) {
    return isDemoUser ? DEMO_DAILY_RECOMMENDATIONS : null;
  }

  const query = new URLSearchParams();
  if (targetPhone) query.append("phone", targetPhone);
  if (targetEmail) query.append("email", targetEmail);
  if (limit) query.append("limit", limit.toString());
  if (options.date) query.append("date", options.date);

  const activeUrl = normalizeCoreApiUrl(config.apiUrl || CORE_API_URL);
  const activeKey = config.apiKey || CORE_API_KEY;
  const proxyUrl = getCoreProxyUrl(`user/daily-recommendations?${query.toString()}`);

  try {
    const res = await fetch(proxyUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-core-target-url": activeUrl,
        "x-core-api-key": activeKey,
        "x-api-key": activeKey
      }
    });

    if (!res.ok) {
      console.warn(`[coreServicesClient] Error ${res.status} al consultar daily-recommendations vía proxy`);
      return isDemoUser ? DEMO_DAILY_RECOMMENDATIONS : null;
    }

    const data = await res.json();
    if (data && !isDemoUser) {
      if (Array.isArray(data.recommendations)) {
        data.recommendations = data.recommendations.filter(
          (rec) => !isCannedRecommendationMessage(rec?.message || rec?.recommendation)
        );
      }
      if (data.activeNudge && isCannedActiveNudge(data.activeNudge)) {
        data.activeNudge = null;
      }
    }
    return data;
  } catch (error) {
    console.warn("[coreServicesClient] Error de red en daily-recommendations vía proxy:", error.message);
    return isDemoUser ? DEMO_DAILY_RECOMMENDATIONS : null;
  }
}

/**
 * POST /api/user/sync-web-user
 * Sincroniza un usuario individual o lote a WE360 Core Services
 */
export async function syncWebUser(payload) {
  const config = getCoreServicesConfig();

  if (config.forceFallback) {
    return {
      status: "Success",
      processed: Array.isArray(payload?.users) ? payload.users.length : 1,
      successful: Array.isArray(payload?.users) ? payload.users.length : 1,
      results: [
        {
          success: true,
          phone: payload?.phone || payload?.users?.[0]?.phone || "584124281735",
          name: payload?.name || payload?.users?.[0]?.name || "Colaborador Sincronizado (Offline)",
          currentPhase: 5
        }
      ]
    };
  }

  const activeUrl = normalizeCoreApiUrl(config.apiUrl || CORE_API_URL);
  const activeKey = config.apiKey || CORE_API_KEY;
  const proxyUrl = getCoreProxyUrl("user/sync-web-user");

  try {
    let data = null;
    try {
      const res = await fetch(proxyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-core-target-url": activeUrl,
          "x-core-api-key": activeKey,
          "x-api-key": activeKey
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        data = await res.json();
      }
    } catch (proxyErr) {
      console.warn("[coreServicesClient] Proxy fetch falló, probando directo:", proxyErr.message);
    }

    if (!data) {
      const directRes = await fetch(`${activeUrl}/user/sync-web-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": activeKey
        },
        body: JSON.stringify(payload)
      });
      if (directRes.ok) {
        data = await directRes.json();
      } else {
        const errBody = await directRes.text();
        throw new Error(`Sync directo falló (${directRes.status}): ${errBody}`);
      }
    }

    return data;
  } catch (error) {
    console.warn("[coreServicesClient] Error en syncWebUser vía proxy y directo:", error.message);
    // Simulación exitosa para entornos locales sin backend Core activo
    return {
      status: "Success",
      processed: Array.isArray(payload?.users) ? payload.users.length : 1,
      successful: Array.isArray(payload?.users) ? payload.users.length : 1,
      results: [
        {
          success: true,
          phone: payload?.phone || payload?.users?.[0]?.phone || "584124281735",
          name: payload?.name || payload?.users?.[0]?.name || "Colaborador Sincronizado",
          currentPhase: 5
        }
      ]
    };
  }
}

export const COUNTRY_CODES = [
  // Principales mercados y Centroamérica / Caribe / Norteamérica
  { code: "+507", label: "🇵🇦 +507 (Panamá)", country: "Panamá" },
  { code: "+57", label: "🇨🇴 +57 (Colombia)", country: "Colombia" },
  { code: "+58", label: "🇻🇪 +58 (Venezuela)", country: "Venezuela" },
  { code: "+52", label: "🇲🇽 +52 (México)", country: "México" },
  { code: "+506", label: "🇨🇷 +506 (Costa Rica)", country: "Costa Rica" },
  { code: "+1", label: "🇺🇸/🇨🇦/🇩🇴 +1 (EE.UU./Canadá/Caribe)", country: "EE.UU. / Canadá / Caribe" },
  // Sudamérica
  { code: "+54", label: "🇦🇷 +54 (Argentina)", country: "Argentina" },
  { code: "+55", label: "🇧🇷 +55 (Brasil)", country: "Brasil" },
  { code: "+56", label: "🇨🇱 +56 (Chile)", country: "Chile" },
  { code: "+51", label: "🇵🇪 +51 (Perú)", country: "Perú" },
  { code: "+593", label: "🇪🇨 +593 (Ecuador)", country: "Ecuador" },
  { code: "+598", label: "🇺🇾 +598 (Uruguay)", country: "Uruguay" },
  { code: "+595", label: "🇵🇾 +595 (Paraguay)", country: "Paraguay" },
  { code: "+591", label: "🇧🇴 +591 (Bolivia)", country: "Bolivia" },
  // Resto de Centroamérica y Caribe
  { code: "+502", label: "🇬🇹 +502 (Guatemala)", country: "Guatemala" },
  { code: "+503", label: "🇸🇻 +503 (El Salvador)", country: "El Salvador" },
  { code: "+504", label: "🇭🇳 +504 (Honduras)", country: "Honduras" },
  { code: "+505", label: "🇳🇮 +505 (Nicaragua)", country: "Nicaragua" },
  { code: "+53", label: "🇨🇺 +53 (Cuba)", country: "Cuba" },
  { code: "+509", label: "🇭🇹 +509 (Haití)", country: "Haití" },
  { code: "+501", label: "🇧🇿 +501 (Belice)", country: "Belice" },
  // Europa
  { code: "+34", label: "🇪🇸 +34 (España)", country: "España" },
  { code: "+39", label: "🇮🇹 +39 (Italia)", country: "Italia" },
  { code: "+351", label: "🇵🇹 +351 (Portugal)", country: "Portugal" },
  { code: "+33", label: "🇫🇷 +33 (Francia)", country: "Francia" },
  { code: "+44", label: "🇬🇧 +44 (Reino Unido)", country: "Reino Unido" },
  { code: "+49", label: "🇩🇪 +49 (Alemania)", country: "Alemania" },
  { code: "+41", label: "🇨🇭 +41 (Suiza)", country: "Suiza" },
  { code: "+31", label: "🇳🇱 +31 (Países Bajos)", country: "Países Bajos" },
  { code: "+32", label: "🇧🇪 +32 (Bélgica)", country: "Bélgica" },
  { code: "+43", label: "🇦🇹 +43 (Austria)", country: "Austria" },
  { code: "+353", label: "🇮🇪 +353 (Irlanda)", country: "Irlanda" },
  { code: "+46", label: "🇸🇪 +46 (Suecia)", country: "Suecia" },
  { code: "+47", label: "🇳🇴 +47 (Noruega)", country: "Noruega" },
  { code: "+45", label: "🇩🇰 +45 (Dinamarca)", country: "Dinamarca" },
  { code: "+48", label: "🇵🇱 +48 (Polonia)", country: "Polonia" },
  { code: "+30", label: "🇬🇷 +30 (Grecia)", country: "Grecia" },
  // Resto del Mundo (Hubs Globales)
  { code: "+972", label: "🇮🇱 +972 (Israel)", country: "Israel" },
  { code: "+971", label: "🇦🇪 +971 (Emiratos Árabes)", country: "Emiratos Árabes Unidos" },
  { code: "+61", label: "🇦🇺 +61 (Australia)", country: "Australia" },
  { code: "+64", label: "🇳🇿 +64 (Nueva Zelanda)", country: "Nueva Zelanda" },
  { code: "+81", label: "🇯🇵 +81 (Japón)", country: "Japón" },
  { code: "+82", label: "🇰🇷 +82 (Corea del Sur)", country: "Corea del Sur" },
  { code: "+86", label: "🇨🇳 +86 (China)", country: "China" },
  { code: "+91", label: "🇮🇳 +91 (India)", country: "India" },
  { code: "+65", label: "🇸🇬 +65 (Singapur)", country: "Singapur" },
  { code: "+27", label: "🇿🇦 +27 (Sudáfrica)", country: "Sudáfrica" },
];

const KNOWN_PREFIX_PATTERN = new RegExp(
  `^(${COUNTRY_CODES.map((c) => c.code.replace(/\D/g, ""))
    .sort((a, b) => b.length - a.length)
    .join("|")})`
);

/**
 * Normaliza un número de WhatsApp al formato de dígitos internacionales (ej: "50765551234")
 * requerido por el backend Core Services (Leandro) para vincular el bot de WhatsApp.
 */
export function normalizeWhatsAppPhone(rawInput, selectedCountryCode = "+507") {
  const trimmed = String(rawInput || "").trim();
  const digitsOnly = trimmed.replace(/\D/g, "");
  if (!digitsOnly) return "";

  const ccDigits = String(selectedCountryCode || "+507").replace(/\D/g, "");

  // Si el usuario escribió explícitamente con '+' (ej: "+39 340 123 4567"), ya incluye código de país
  if (trimmed.startsWith("+")) {
    return digitsOnly;
  }

  // Si escribió con prefijo internacional '00' (ej: "0039 340 123 4567")
  if (trimmed.startsWith("00") && digitsOnly.length >= 10) {
    return digitsOnly.replace(/^00/, "");
  }

  // Si ya comienza con el código de país seleccionado y tiene longitud internacional completa
  if (ccDigits && digitsOnly.startsWith(ccDigits) && digitsOnly.length >= ccDigits.length + 7) {
    return digitsOnly;
  }

  // Si tiene 11+ dígitos y comienza con un código conocido (ej. guardado previamente como "50765551234" o "573150369033")
  if (digitsOnly.length >= 11 && KNOWN_PREFIX_PATTERN.test(digitsOnly)) {
    return digitsOnly;
  }

  // Para Italia (+39), el '0' inicial en números fijos no se elimina, pero en móviles y resto de países se limpia el 0 trunk
  const localClean = ccDigits === "39" ? digitsOnly : digitsOnly.replace(/^0+/, "");
  return `${ccDigits}${localClean}`;
}

const SORTED_COUNTRY_CODES = [...COUNTRY_CODES]
  .map((c) => ({ ...c, dialDigits: c.code.replace(/\D/g, "") }))
  .sort((a, b) => b.dialDigits.length - a.dialDigits.length);

const EIGHT_DIGIT_TWO_CC = new Set(["45", "47", "53", "65"]);

/**
 * Separa un número de WhatsApp (guardado en formato E.164 con o sin '+', ej: "50766723756")
 * en su código de país ("+507") y su número local ("66723756") para pre-cargar selectores de UI.
 *
 * @param {string} rawInput - Teléfono crudo o E.164
 * @param {string} fallbackCountryCode - Código de país por defecto si el número ya es local (ej: "+507")
 * @returns {{ countryCode: string, localNumber: string }}
 */
export function splitWhatsAppPhone(rawInput, fallbackCountryCode = "+507") {
  const rawFallbackDigits = String(fallbackCountryCode || "+507").replace(/\D/g, "") || "507";
  const normalizedFallback = `+${rawFallbackDigits}`;

  const trimmed = String(rawInput || "").trim();
  if (!trimmed) {
    return { countryCode: normalizedFallback, localNumber: "" };
  }

  const digitsOnly = trimmed.replace(/\D/g, "");
  if (!digitsOnly) {
    return { countryCode: normalizedFallback, localNumber: "" };
  }

  // Caso 1: Prefijo internacional explícito ('+' o '00')
  if (trimmed.startsWith("+") || (trimmed.startsWith("00") && digitsOnly.length >= 10)) {
    const intlDigits = trimmed.startsWith("00") ? digitsOnly.replace(/^00/, "") : digitsOnly;
    for (const c of SORTED_COUNTRY_CODES) {
      if (intlDigits.startsWith(c.dialDigits) && intlDigits.length > c.dialDigits.length) {
        return {
          countryCode: c.code,
          localNumber: intlDigits.slice(c.dialDigits.length),
        };
      }
    }
  }

  // Caso 2: Dígitos E.164 completos sin '+' (ej: "50766723756", "573150369033", "13055551234")
  if (digitsOnly.length >= 10) {
    for (const c of SORTED_COUNTRY_CODES) {
      if (!digitsOnly.startsWith(c.dialDigits)) continue;
      const localPart = digitsOnly.slice(c.dialDigits.length);
      const ccLen = c.dialDigits.length;

      const isValidLocalLength =
        (ccLen === 3 && localPart.length >= 7 && localPart.length <= 10) ||
        (ccLen === 2 &&
          ((EIGHT_DIGIT_TWO_CC.has(c.dialDigits) && localPart.length === 8) ||
            (localPart.length >= 9 && localPart.length <= 11))) ||
        (ccLen === 1 && localPart.length === 10);

      if (isValidLocalLength) {
        return {
          countryCode: c.code,
          localNumber: localPart,
        };
      }
    }
  }

  // Caso 3: Ya es un número nacional/local sin código de país
  return {
    countryCode: normalizedFallback,
    localNumber: trimmed,
  };
}


/**
 * Valida que el número de WhatsApp tenga un formato telefónico internacional E.164 real
 * y no sea una secuencia ficticia o truncada.
 *
 * @param {string} rawInput - Número ingresado por el usuario
 * @param {string} selectedCountryCode - Prefijo seleccionado (ej. "+507", "+39")
 * @returns {{ valid: boolean, normalized: string, cleanPhone: string, error: string | null }}
 */
export function validateWhatsAppPhone(rawInput, selectedCountryCode = "+507") {
  const trimmed = String(rawInput || "").trim();
  if (!trimmed) {
    return {
      valid: false,
      normalized: "",
      cleanPhone: "",
      error: "Por favor ingresa tu número de WhatsApp.",
    };
  }

  // Rechazar si contiene letras
  if (/[a-zA-Z]/.test(trimmed)) {
    return {
      valid: false,
      normalized: "",
      cleanPhone: "",
      error: "El número de teléfono solo debe contener dígitos.",
    };
  }

  const rawDigits = trimmed.replace(/\D/g, "");
  const normalized = normalizeWhatsAppPhone(trimmed, selectedCountryCode);
  const ccDigits = String(selectedCountryCode || "+507").replace(/\D/g, "");

  // Extraer la porción nacional aproximada para validar longitud y patrones repetidos
  const localDigits = trimmed.startsWith("+")
    ? normalized.replace(KNOWN_PREFIX_PATTERN, "")
    : normalized.startsWith(ccDigits)
      ? normalized.slice(ccDigits.length)
      : rawDigits;

  if (rawDigits.length < 7 || localDigits.length < 6 || normalized.length < 8 || normalized.length > 15) {
    return {
      valid: false,
      normalized,
      cleanPhone: normalized,
      error: "Ingresa un número de WhatsApp válido con su código de país (entre 7 y 15 dígitos).",
    };
  }

  // Rechazar dígitos repetidos idénticos (ej: 0000000, 1111111, 55555555) o secuencias triviales
  if (
    /^(\d)\1{5,}$/.test(localDigits) ||
    /^(0123456|1234567|12345678|123456789|9876543|8765432)/.test(localDigits)
  ) {
    return {
      valid: false,
      normalized,
      cleanPhone: normalized,
      error: "El número ingresado parece inválido. Por favor ingresa tu número real de WhatsApp.",
    };
  }

  return {
    valid: true,
    normalized,
    cleanPhone: normalized,
    error: null,
  };
}


